<template>
  <div
    id="avatar"
    class="avatar"
    :class="{ 'avatar--no-border': noBorder, 'avatar--square': square }"
    :style="{ width: `${width}px`, height: `${height}px` }"
  >
    <UIBaseTransition>
      <UIProgressSpinner v-if="loading" />
      <span v-else-if="initials && !isValidUrl && !isBase64" class="avatar__placeholder">
        {{ initials }}
      </span>
      <img v-else-if="src" :src="src" :alt="alt" class="object-cover" />
      <img v-else src="/images/avatar.png" :alt="alt" />
    </UIBaseTransition>
  </div>
</template>

<script setup lang="ts">
import type { ImgHTMLAttributes } from 'vue'
import { string } from 'zod'
import type { User } from '~/types'

const props = defineProps<{
  src: ImgHTMLAttributes['src']
  alt?: ImgHTMLAttributes['alt']
  width?: number | string
  height?: number | string
  user?: User
  noBorder?: boolean
  square?: boolean
  loading?: boolean
}>()

const initials = computed(() => {
  if (!props.user?.firstName || !props.user?.lastName) return ''

  return props.user.firstName[0] + props.user.lastName[0]
})

const isValidUrl = computed(() => string().url().safeParse(props.src).success)

const isBase64 = computed(() => Boolean(props.src?.startsWith('data:image')))
</script>

<style scoped lang="scss">
@use '@/styles/mixins.scss' as *;
@use '@/styles/variables.scss' as *;

.avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid $black-100;
  background: $gold-primary-20;
  overflow: hidden;
  flex-shrink: 0;
  text-transform: uppercase;

  @media screen and (max-width: 1023px) {
    background-color: $gold-primary-50;
  }

  &--no-border {
    border: unset;
  }

  &--square {
    border-radius: 8px;
  }

  img {
    width: 100%;
    height: 100%;
  }
}
</style>
